import * as React from 'react';
import cx from 'classnames';
import { Flex } from '@common/components/Flex';
import { I18n } from 'react-redux-i18n';
import { getTestAttributes } from '@common/utils/tests';
import { EmailTemplateListDto } from '@common/dto/email-template/list';
import { FormControl } from '@common/components/form-control';
import { Checkbox } from '@common/components/checkbox';
import { Editor } from '@common/components/email-template/components/editor';
import { ToolbarCustomButton } from '@common/components/text-editor/text-editor.types';

import './template-management.styles.scss';

export type TemplateManagementProps = {
  disabled?: boolean;
  className?: string;
  template?: EmailTemplateListDto;
  onTemplateChange: (template: EmailTemplateListDto) => void;
  toolbarCustomButtons?: Array<ToolbarCustomButton>;
  onFieldButtonClick?: (params: { addContent: (text: string) => void }) => void;
};

export const TemplateManagement: React.FC<TemplateManagementProps> = (props) => {
  const { onTemplateChange, template, className, disabled, toolbarCustomButtons, onFieldButtonClick } = props;
  const [name, setName] = React.useState<string>('');
  const [subject, setSubject] = React.useState<string>('');
  const [body, setBody] = React.useState<string>('');

  React.useEffect(() => {
    setName(template?.name ?? '');
    setSubject(template?.subject ?? '');
    setBody(template?.body ?? '');
  }, [template?.id]);

  const handleChange = React.useCallback(() => {
    if (!template) {
      return;
    }

    onTemplateChange({
      ...template,
      name: name.trim(),
      subject: subject.trim(),
      body,
    });
  }, [name, subject, body, onTemplateChange, template]);

  const handleLockedChange = React.useCallback((locked: boolean) => {
    if (!template) {
      return;
    }

    onTemplateChange({
      ...template,
      locked
    });
  }, [onTemplateChange, template]);

  const handleBodyChange = React.useCallback((value: string) => {
    if (!template) {
      return;
    }

    setBody(value);
    
    onTemplateChange({
      ...template,
      body: value,
    });
  }, [template, onTemplateChange]);

  return (
    <div className={cx("EmailTemplatesTemplateManagement", { 'EmailTemplatesTemplateManagement--disabled': disabled }, className)}>
      <Flex alignItems="flex-start" className="EmailTemplatesTemplateManagement__section">
        <span className="EmailTemplatesTemplateManagement__section__label">
          {I18n.t('Template Name')}
        </span>
        <FormControl
          {...getTestAttributes({ id: 'tx-email-formats-management-name' })}
          className="EmailTemplatesTemplateManagement__section__input"
          value={name}
          onChange={(e) => setName((e.target as unknown as { value: string }).value)}
          onBlur={handleChange}
        />
      </Flex>

      <Flex alignItems="flex-start" className="EmailTemplatesTemplateManagement__section">
        <span className="EmailTemplatesTemplateManagement__section__label">
          {I18n.t('Subject')}
        </span>
        <FormControl
          {...getTestAttributes({ id: 'tx-email-formats-management-subject' })}
          className="EmailTemplatesTemplateManagement__section__input"
          value={subject}
          onChange={(e) => setSubject((e.target as unknown as { value: string }).value)}
          onBlur={handleChange}
        />
      </Flex>

      <Flex alignItems="flex-start" className="EmailTemplatesTemplateManagement__section">
        <span className="EmailTemplatesTemplateManagement__section__label">
          {I18n.t('Body')}
        </span>
        <Editor
          classes={{
            root: 'EmailTemplatesTemplateManagement__section__editor',
            workAreaInternal: 'EmailTemplatesTemplateManagement__section__editor__work-area',
            textarea: 'EmailTemplatesTemplateManagement__section__editor__textarea',
          }}
          value={body}
          onChange={handleBodyChange}
          toolbarCustomButtons={toolbarCustomButtons}
          onFieldButtonClick={onFieldButtonClick}
        />
      </Flex>

      <Flex alignItems="flex-start" className="EmailTemplatesTemplateManagement__section">
        <span className="EmailTemplatesTemplateManagement__section__label">
          {I18n.t('Locked (IRB Approved)')}
        </span>
        <Checkbox
          {...getTestAttributes({ id: 'cbx-email-formats-management-locked-input' })}
          className="EmailTemplatesTemplateManagement__section__checkbox"
          checked={template?.locked ?? false}
          onChange={handleLockedChange}
        />
      </Flex>
    </div>
  );
};